import _ from 'lodash'
import {useSearchParams} from 'next/navigation'
import {FC, useMemo, useRef, useState, useTransition} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {getBlogContent} from '@/content/blog/blog'
import {useTranslation} from '@/i18n'
import {SECTION_PADDING_DESKTOP, SECTION_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {getBlogOverviewPath} from '@/utils/blog/helpers'
import {BlogOverview} from '@/utils/contentful'
import {useRouter} from '@/utils/hooks/useRouter'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {QUERY_PARAM_PAGINATION_KEY} from '../common/content/pagination/constants'
import {buildPaginationUrl} from '../common/content/pagination/paginate'
import {Pagination} from '../common/content/pagination/Pagination'
import {Section} from '../common/content/Section'
import {useMobile} from '../context/MobileProvider'
import {BlogPreview} from './BlogPreview'
import {BlogPreviewListSkeleton} from './BlogPreviewSkeleton'
import {BLOGS_PER_PAGE} from './constants'
import {BlogSidebar} from './sidebar/BlogSidebar'

type BlogOverviewLayoutProps = {
  blogs: BlogOverview[]
  currentPage: number
  numberOfPages: number
  totalItems: number
}

export const BlogOverviewLayout: FC<React.PropsWithChildren<BlogOverviewLayoutProps>> = ({
  blogs,
  currentPage,
  numberOfPages,
  totalItems,
}) => {
  const {t} = useTranslation()
  const {articleListTitle} = getBlogContent()
  const router = useRouter()
  const searchParams = useSearchParams()
  const sectionRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const isMobile = useMobile()
  // Paging is a server round trip now — tracks whether the next page is still in flight, so we
  // can show a skeleton instead of stale content.
  const [isPending, startTransition] = useTransition()
  // Row heights vary with how much title/excerpt text wraps, so a fixed skeleton can't match
  // every viewport. Measuring the outgoing rows keeps the swap from shifting anything.
  const [skeletonRows, setSkeletonRows] = useState<number[]>([])
  // Derived from skeletonRows rather than tracked separately, so the two can't drift out of sync.
  const listHeight = useMemo(() => {
    if (!skeletonRows.length) {
      return undefined
    }
    const rowGap = parseFloat(isMobile ? theme.spacing[4] : theme.spacing[5])
    return _.sum(skeletonRows) + rowGap * (skeletonRows.length - 1)
  }, [skeletonRows, isMobile])

  const isPaginationEnabled = numberOfPages > 1

  const goToPage = (page: number) => {
    // Avoid re-navigating (and flashing a skeleton) when the target page is already active.
    if (page === currentPage) {
      return
    }
    const url = buildPaginationUrl(
      router.pathname,
      new URLSearchParams(searchParams?.toString()),
      page,
      QUERY_PARAM_PAGINATION_KEY,
      true,
    )

    // Every page has BLOGS_PER_PAGE rows except a possibly shorter last one — using the outgoing
    // page's count instead broke the skeleton size around that shorter page.
    const targetRowCount =
      page === numberOfPages ? totalItems - (numberOfPages - 1) * BLOGS_PER_PAGE : BLOGS_PER_PAGE
    const measuredHeights = [
      ...(listRef.current?.querySelectorAll<HTMLElement>('article') ?? []),
    ].map((article) => article.offsetHeight)
    const averageHeight = measuredHeights.length ? _.mean(measuredHeights) : 0
    const rows = Array.from(
      {length: targetRowCount},
      (_unused, index) => measuredHeights[index] ?? averageHeight,
    )

    setSkeletonRows(rows)
    startTransition(() => {
      void router.push(url, {scroll: false})
    })
  }

  const sectionOffset = isMobile ? -40 : 0

  return (
    <>
      <Section ref={sectionRef}>
        <div className="content">
          {(blogs.length > 0 || isPending) && (
            <div className="main">
              <div className="list-title">
                <Text variant={TextVariant.h2}>{t(articleListTitle)}</Text>
              </div>
              {isPending ? (
                <div className="pending">
                  <BlogPreviewListSkeleton
                    rows={skeletonRows.length ? skeletonRows : Array(blogs.length).fill(0)}
                  />
                </div>
              ) : (
                <div ref={listRef}>
                  {blogs.map((blog) => (
                    <BlogPreview key={blog.slug} blog={blog} linkHref={getBlogOverviewPath(blog)} />
                  ))}
                </div>
              )}
              {isPaginationEnabled && (
                <div className="pagination">
                  <Pagination
                    sectionRef={sectionRef}
                    sectionOffset={sectionOffset}
                    currentPage={currentPage}
                    numberOfPages={numberOfPages}
                    setCurrentPage={goToPage}
                    isPending={isPending}
                  />
                </div>
              )}
            </div>
          )}
          <BlogSidebar fullWidth={!blogs.length} />
        </div>
      </Section>

      <style jsx>{`
        .list-title {
          margin-bottom: ${theme.spacing[6]};
        }
        .content {
          display: flex;
          position: relative;
          padding: ${SECTION_PADDING_DESKTOP} 0;
        }
        .main {
          flex: 1;
          min-width: 0;
        }
        .pending {
          overflow: hidden;
          height: ${listHeight}px;
        }
        .pagination {
          margin-top: ${theme.spacing[7]};
        }
        @media ${TABLET_SCREENSIZE} {
          .list-title {
            text-align: center;
            margin-bottom: ${theme.spacing[5]};
          }
          .content {
            display: block;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .content {
            padding: ${SECTION_PADDING_MOBILE} 0;
          }
          .pagination {
            margin-top: ${theme.spacing[6]};
          }
        }
      `}</style>
    </>
  )
}
